import { Play, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AssignedTest,
  setIsInstructions,
  setOpenCard,
  setSelectedTestData
} from '../../../../redux/Slices/CloudPrepSlice';
import { setIsStartTest } from '../../../../redux/Slices/QuestionsSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/store/hooks';
import {
  useDiscardCloudPrepTestMutation,
  useGetAssignedTestsQuery,
  useLazyFetchCloudPrepUserTestQuery,
  useResumeCloudPrepTestMutation
} from '../../../../services/cloudPrepApi';

const AITestList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { assignedTests } = useAppSelector(state => state.root.cloudPrep);
  const { id, subId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [resumeCloudPrepTest] = useResumeCloudPrepTestMutation();
  const [discardCloudPrepTest] = useDiscardCloudPrepTestMutation();
  const [fetchUserTest] = useLazyFetchCloudPrepUserTestQuery();
  useGetAssignedTestsQuery(
    {
      company: id,
      testType: subId,
      totalTestCount: 10
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const handleStartTest = async (test: AssignedTest) => {
    try {
      setIsLoading(true);

      dispatch(
        setSelectedTestData({
          ...test,
          testType: test.testType || subId
        })
      );
      dispatch(setOpenCard(test.testType || subId || null));

      dispatch(setIsInstructions(true));

      navigate(`/cloud-prep/instructions/${test.id}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResumeTest = async (
    userTestId: AssignedTest['userTestId'],
    testSetId: AssignedTest['testSetId'],
    test: AssignedTest
  ) => {
    try {
      setIsLoading(true);

      await resumeCloudPrepTest({
        userTestId,
        testSetId
      }).unwrap();

      dispatch(setSelectedTestData(test));
      dispatch(setIsStartTest(true));
      dispatch(setIsInstructions(false));

      navigate(`/cloud-prep/tests/${userTestId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisacardTest = async (
    userTestId: AssignedTest['userTestId'],
    testSetId: AssignedTest['testSetId']
  ) => {
    try {
      setIsLoading(true);

      await discardCloudPrepTest({
        userTestId,
        testSetId,
        testType: subId
      }).unwrap();

      // dispatch(resetCloudPrepState());
      dispatch(setIsStartTest(false));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (
    action: 'View Results' | 'Resume Test' | 'Discard',
    test: AssignedTest
  ) => {
    const { userTestId, testSetId } = test;

    if (action === 'View Results') {
      navigate(`/cloud-prep-results/${userTestId}/${test.userId}/false`);
      return;
    }

    if (action === 'Resume Test') {
      await handleResumeTest(userTestId, testSetId, test);
    }

    if (action === 'Discard') {
      await handleDisacardTest(userTestId, testSetId);
    }
  };

  const renderActionButtons = (test: AssignedTest) => {
    switch (test?.testStatus) {
      case 'NOT_YET':
        return (
          <button
            onClick={() => handleStartTest(test)}
            disabled={isLoading}
            className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-alpha'
          >
            <Play className='h-4 w-4' />
            Start Test
          </button>
        );

      case 'STARTED':
        return (
          <button
            onClick={() => handleAction('Discard', test)}
            disabled={isLoading}
            className='flex w-full items-center justify-center gap-2 rounded-lg bg-[#A4031F] px-4 py-2.5 text-sm text-white'
          >
            <Trash2 className='h-4 w-4' />
            Discard Test
          </button>
        );

      case 'COMPLETED':
        return (
          <button
            onClick={() => handleAction('View Results', test)}
            disabled={isLoading}
            className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm text-white'
          >
            View Results
          </button>
        );

      default:
        return null;
    }
  };
  return (
    <div className='p-4'>
      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {assignedTests?.data?.map(test => {
          return (
            <div
              key={test.id}
              className='rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md'
            >
              <div className='cursor-pointer p-4 sm:p-5'>
                <div className='mb-3 flex flex-row items-center gap-3 sm:justify-between'>
                  <h4 className='text-lg font-semibold sm:text-xl'>
                    {test?.testName}
                  </h4>
                </div>
                <div className='justify-self-end'>
                  {renderActionButtons(test)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AITestList;
